import { AlertTriangle, ExternalLink, RefreshCw } from "lucide-react";

import {
  googleCreditOnboardingHref,
  providerCreditPresentation,
  type ProviderCreditStatus,
  type ProviderCreditTone,
} from "@/lib/provider-credit";

const TONE_STYLES: Record<
  ProviderCreditTone,
  {
    border: string;
    background: string;
    badgeBackground: string;
    badgeColor: string;
  }
> = {
  neutral: {
    border: "rgba(0,0,0,0.10)",
    background: "rgba(255,255,255,0.36)",
    badgeBackground: "rgba(0,0,0,0.06)",
    badgeColor: "rgba(0,0,0,0.60)",
  },
  info: {
    border: "rgba(37,99,235,0.22)",
    background: "rgba(239,246,255,0.62)",
    badgeBackground: "rgba(37,99,235,0.10)",
    badgeColor: "#1d4ed8",
  },
  positive: {
    border: "rgba(5,150,105,0.22)",
    background: "rgba(236,253,245,0.62)",
    badgeBackground: "rgba(5,150,105,0.10)",
    badgeColor: "#047857",
  },
  warning: {
    border: "rgba(217,119,6,0.26)",
    background: "rgba(255,251,235,0.68)",
    badgeBackground: "rgba(217,119,6,0.12)",
    badgeColor: "#a16207",
  },
  danger: {
    border: "rgba(220,38,38,0.22)",
    background: "rgba(254,242,242,0.66)",
    badgeBackground: "rgba(220,38,38,0.10)",
    badgeColor: "#b91c1c",
  },
};

export function ProviderCreditStatusCard({
  providerName = "Google Cloud",
  providerID,
  status,
  setupHref,
  showSetupAction = true,
  refreshing = false,
  onRefresh,
  compact = false,
}: {
  providerName?: string;
  providerID?: string;
  status: ProviderCreditStatus;
  setupHref?: string;
  showSetupAction?: boolean;
  refreshing?: boolean;
  onRefresh?: () => void;
  compact?: boolean;
}) {
  const view = providerCreditPresentation(status);
  const resolvedSetupHref =
    setupHref || googleCreditOnboardingHref(providerID);
  const tone = TONE_STYLES[view.tone];
  const warning = view.tone === "warning" || view.tone === "danger";
  const setupAction = view.action === "setup";
  const refreshAction = view.action === "retry" && onRefresh;

  return (
    <section
      aria-label={`${providerName} promotional credit balance status`}
      aria-live={warning ? "polite" : undefined}
      role={warning ? "status" : undefined}
      className={compact ? "rounded-xl border p-3" : "rounded-2xl border p-4"}
      style={{ borderColor: tone.border, background: tone.background }}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p
            className="text-[10px] font-semibold uppercase tracking-[0.16em]"
            style={{ color: "rgba(0,0,0,0.42)" }}
          >
            Google promotional credit balance
          </p>
          <p
            className="mt-1 break-words text-[13px] font-semibold"
            style={{ color: "rgba(0,0,0,0.78)" }}
          >
            {view.primary}
          </p>
        </div>
        <span
          aria-label={`Accuracy: ${view.badge}`}
          className="inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold"
          style={{
            background: tone.badgeBackground,
            color: tone.badgeColor,
          }}
        >
          {warning ? <AlertTriangle aria-hidden="true" size={11} /> : null}
          {view.badge}
        </span>
      </div>

      <p
        className="mt-2 text-[11px] leading-relaxed"
        style={{ color: "rgba(0,0,0,0.52)" }}
      >
        {view.secondary}
      </p>
      <p
        className="mt-2 text-[10px] leading-relaxed"
        style={{ color: "rgba(0,0,0,0.42)" }}
      >
        Balance information only — not a payment card, UC wallet balance, or
        spendable GENRL credit.
      </p>

      {!compact && view.details.length > 0 ? (
        <ul
          className="mt-2 space-y-1 text-[10px]"
          style={{ color: "rgba(0,0,0,0.42)" }}
        >
          {view.details.map((detail) => (
            <li key={detail}>{detail}</li>
          ))}
        </ul>
      ) : null}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {setupAction && showSetupAction ? (
          <a
            href={resolvedSetupHref}
            className="inline-flex min-h-8 items-center rounded-lg px-3 text-[11px] font-semibold"
            style={{ background: "rgba(14,12,8,0.88)", color: "#fff" }}
          >
            Set up Google promotional credit visibility
          </a>
        ) : null}
        {refreshAction ? (
          <button
            type="button"
            onClick={onRefresh}
            disabled={refreshing}
            className="inline-flex min-h-8 items-center gap-1.5 rounded-lg px-3 text-[11px] font-semibold disabled:cursor-not-allowed disabled:opacity-60"
            style={{ background: "rgba(14,12,8,0.88)", color: "#fff" }}
          >
            <RefreshCw
              aria-hidden="true"
              size={12}
              className={refreshing ? "animate-spin" : ""}
            />
            {refreshing ? "Refreshing" : "Retry"}
          </button>
        ) : null}
        <a
          href={view.consoleURL}
          target="_blank"
          rel="noreferrer"
          className="inline-flex min-h-8 items-center gap-1.5 rounded-lg px-3 text-[11px] font-semibold"
          style={{
            border: "1px solid rgba(0,0,0,0.10)",
            color: "rgba(0,0,0,0.68)",
          }}
        >
          View in Google
          <ExternalLink aria-hidden="true" size={11} />
        </a>
      </div>
    </section>
  );
}
